import { type ReactNode, useCallback, useContext, useEffect, useMemo, useState } from "react";

import { Button, InfoBar, Menu, Modal } from "@shapr3d/frontend-lib";
import { useParams } from "react-router-dom";

import { DBAvailableContext } from "../../contexts/DBAvailableProvider";
import useNavigation from "../../hooks/navigation";
import useAccount, {
  useCancelSubscription,
  useMutatePaddleAutoRenew,
} from "../../hooks/useAccount";
import type { Subscription } from "../../hooks/useAccountTypes";
import { useHasStudioPricing } from "../../hooks/usePriceConfig";
import { useTeam } from "../../hooks/useTeams";
import { useTracker } from "../../hooks/useTracker";
import useTranslation from "../../hooks/useTranslation";
import {
  ElementClickAction,
  ElementClickLocation,
  ModalShownAction,
} from "../../lib/TrackingAdapter";
import { SearchParam, SearchParamIntent, SubscriptionTier, SubscriptionType } from "../../types";
import CTARow from "../CTARow/CTARow";
import { CTARowDivider } from "../CTARowDivider/CTARowDivider";
import { GenericErrorModal } from "../Modal/Modal";
import { CancellationFlow } from "../TeamSubscriptions/CancellationFlow/CancellationFlow";
import { SubscriptionExpirationText } from "../TeamSubscriptions/SubscriptionExpirationText/SubscriptionExpirationText";
import {
  getPlanDisplayName,
  getSourceDisplayName,
  getTierDisplayName,
} from "../TeamSubscriptions/subscriptionUtils";

import style from "./SubscriptionInfo.module.css";

const openEduMoreInformation = () => {
  window.open(
    "https://support.shapr3d.com/hc/en-us/articles/115001796629",
    "_blank",
    "noopener,noreferrer"
  );
};

const openPricingPage = () => {
  window.open("https://shapr3d.com/pricing", "_blank", "noopener,noreferrer");
};

export default function SubscriptionInfo({
  subscription,
  hasAppStoreSubscription,
  techTrialAvailable,
  handlePaymentInfoUpdate,
  subscriptionCTA,
}: {
  subscription: Subscription;
  hasAppStoreSubscription: boolean;
  techTrialAvailable: boolean;
  handlePaymentInfoUpdate: (subscription: Subscription) => void;
  subscriptionCTA?: ReactNode;
}) {
  const { data: accountData } = useAccount();
  const { t, m } = useTranslation();
  const tracker = useTracker();
  const navigate = useNavigation();
  const { isDBDown } = useContext(DBAvailableContext);
  const [isCancellationFlowOpen, setIsCancellationFlowOpen] = useState(false);
  const { type, tier, subscriptionPeriod, expiration } = subscription;
  const navigation = useNavigation();

  const { isStudioTier } = useHasStudioPricing();

  const { teamID } = useParams();
  const { teamData } = useTeam(teamID);

  const planName = useMemo(
    () => getPlanDisplayName(type, subscriptionPeriod, tier, subscription.teamName, t),
    [type, subscriptionPeriod, tier, subscription.teamName, t]
  );

  const expirationDate = new Date(expiration).toLocaleDateString("en-us", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
  const purchaseSource = useMemo(() => getSourceDisplayName(type, t), [type, t]);
  const { mutate: mutatePaddleAutoRenew, isPending: isPaddleAutoRenewPending } =
    useMutatePaddleAutoRenew({ teamID });
  const { mutate: mutateCancelSubscription, isPending: isCancelPending } = useCancelSubscription();
  const [isErrorModalOpen, setIsErrorModalOpen] = useState(false);
  const [isSeatManagementModalOpen, setIsSeatManagementModalOpen] = useState(false);

  const shouldDisplayAutoRenewOnButton =
    subscription.isAutoRenewEditable && !subscription.autorenewal;
  const shouldDisplaySubscriptionCancelButton =
    subscription.autorenewal && (subscription.isAutoRenewEditable || subscription.isOnlyCancelable);

  const isAutoRenewLoading = isPaddleAutoRenewPending;

  const handlePaddleAutoRenewChange = useCallback(
    (autoRenew: boolean) => {
      const paddleSubscriptionID = subscription.paddleId ?? "";
      if (subscription.isAutoRenewEditable) {
        mutatePaddleAutoRenew(
          {
            autoRenew,
            paddleSubscriptionID,
          },
          {
            onError: () => setIsErrorModalOpen(true),
            onSettled: () => {
              setIsCancellationFlowOpen(false);
            },
          }
        );
      } else if (subscription.isOnlyCancelable) {
        mutateCancelSubscription(
          { paddleSubscriptionID },
          {
            onError: () => setIsErrorModalOpen(true),
            onSettled: () => {
              setIsCancellationFlowOpen(false);
            },
          }
        );
      }
    },
    [mutatePaddleAutoRenew, mutateCancelSubscription, subscription]
  );

  const handleAutoRenewChange = useCallback(
    (autoRenew: boolean) => {
      if (subscription.type === SubscriptionType.Paddle) {
        handlePaddleAutoRenewChange(autoRenew);
        return;
      }

      throw new Error("Unsupported subscription type");
    },
    [subscription, handlePaddleAutoRenewChange]
  );

  const isInGracePeriod = useCallback(() => {
    return subscription.gracePeriodExpirationDate !== undefined;
  }, [subscription]);

  const isSoloTier = tier === SubscriptionTier.Individual;
  const isEduSubscription = subscription.type === SubscriptionType.Edu;
  const isSubscriptionManagable =
    shouldDisplaySubscriptionCancelButton ||
    techTrialAvailable ||
    subscription.autorenewal ||
    isEduSubscription;

  const isSubscriptionMigrationEnabled =
    !isInGracePeriod() &&
    (subscription.type === SubscriptionType.Paddle ||
      subscription.type === SubscriptionType.AppStore ||
      subscription.type === SubscriptionType.Gift ||
      subscription.type === SubscriptionType.Edu) &&
    accountData?.availablePlans?.length;

  useEffect(() => {
    if (isSeatManagementModalOpen) {
      tracker.trackModalShown(ModalShownAction.ADD_SEAT_UNAVAILABLE, "individual_subscription");
    }
  }, [isSeatManagementModalOpen, tracker]);

  const handleAddSeats = () => {
    tracker.trackButtonClick({ action: ElementClickAction.ADD_SEATS });
    if (isSubscriptionMigrationEnabled) {
      if (isSoloTier && teamID) {
        navigate.goToTeamSubscription({
          teamID,
          additionalQueryParams: { [SearchParam.Intent]: SearchParamIntent.Upgrade },
        });
        return;
      }
      navigate.goToCheckout({
        selectedTier: tier,
        period: subscription.subscriptionPeriod,
        fromAddSeats: true,
      });
    } else {
      setIsSeatManagementModalOpen(true);
    }
  };

  const handleUpgradeToPro = () => {
    tracker.trackButtonClick({ action: ElementClickAction.UPGRADE_TO_PRO });
    navigate.goToCheckout({
      selectedTier: subscription.tier,
      period: subscription.subscriptionPeriod,
    });
  };

  return (
    <div>
      <CTARow
        title={t(m.TeamSubscriptionPage.SubscriptionTitle)}
        text={planName}
        CTA={subscriptionCTA}
      />
      <CTARowDivider />
      {!isSoloTier && (
        <>
          <CTARow
            title={t(m.TeamSubscriptionPage.Seats)}
            text="1"
            CTA={
              <Menu
                activator={
                  <Button
                    variant="borderless"
                    onClick={() => {
                      tracker.trackButtonClick({
                        action: ElementClickAction.ADD_SEATS,
                        location: ElementClickLocation.MANAGE_SEATS_MENU,
                      });
                    }}
                  >
                    {t(m.TeamSubscriptionPage.Manage)}
                  </Button>
                }
              >
                <Menu.Item onClick={handleAddSeats} closeOnClick>
                  {t(m.TeamSubscriptionPage.AddSeats)}
                </Menu.Item>
                <Menu.Item disabled>{t(m.TeamSubscriptionPage.ManageTeamSize)}</Menu.Item>
              </Menu>
            }
          />
          <CTARowDivider />
        </>
      )}
      <CTARow
        title={
          <SubscriptionExpirationText
            autoRenews={subscription.autorenewal}
            subscriptionType={type}
            expirationDate={expiration}
            hasAppStoreSubscription={hasAppStoreSubscription}
            subscriptionTier={subscription.tier}
          />
        }
        text={expirationDate}
        notification={
          shouldDisplayAutoRenewOnButton && (
            <InfoBar
              text={t(m.TeamSubscriptionPage.TurnOnAutoRenewalInfo, {
                tierName: getTierDisplayName(tier),
              })}
              variant="attention"
              cta={
                <Button
                  variant="grayscale"
                  onClick={() => {
                    handleAutoRenewChange(true);
                    tracker.trackButtonClick({ action: ElementClickAction.TURN_ON_AUTO_RENEWAL });
                  }}
                  disabled={isAutoRenewLoading}
                >
                  {t(m.TeamSubscriptionPage.TurnOnAutoRenewal)}
                </Button>
              }
            ></InfoBar>
          )
        }
        CTA={
          !isDBDown &&
          isSubscriptionManagable && (
            <Menu
              activator={
                <Button
                  variant="borderless"
                  style={{ height: "100%" }}
                  onClick={() => {
                    tracker.trackButtonClick({ action: ElementClickAction.MANAGE_AUTO_RENEWAL });
                  }}
                >
                  {t(m.TeamSubscriptionPage.Manage)}
                </Button>
              }
            >
              {shouldDisplaySubscriptionCancelButton && (
                <Menu.Item
                  onClick={() => {
                    setIsCancellationFlowOpen(true);
                    tracker.trackButtonClick({
                      action: ElementClickAction.CANCEL_SUBSCRIPTION,
                      location: ElementClickLocation.AUTO_RENEWAL_MANAGE_MENU,
                    });
                  }}
                  disabled={isAutoRenewLoading}
                  closeOnClick
                >
                  <div style={{ color: "var(--control-error-label)" }}>
                    {t(m.TeamBilling.CancelSubscription)}
                  </div>
                </Menu.Item>
              )}
              {techTrialAvailable && (
                <Menu.Item onClick={openPricingPage} closeOnClick>
                  {t(m.TeamSubscriptionPage.ChangeToDirectPlan)}
                </Menu.Item>
              )}
              {subscription.autorenewal && (
                <Menu.Item
                  onClick={() => {
                    tracker.trackButtonClick({
                      action: ElementClickAction.UPDATE_PAYMENT_INFORMATION,
                      location: ElementClickLocation.AUTO_RENEWAL_MANAGE_MENU,
                    });
                    return handlePaymentInfoUpdate(subscription);
                  }}
                  closeOnClick
                >
                  {t(m.TeamSubscriptionPage.UpdatePaymentInfo)}
                </Menu.Item>
              )}
              {isEduSubscription && (
                <>
                  <Menu.Item onClick={handleUpgradeToPro} closeOnClick>
                    {isStudioTier
                      ? t(m.TeamSubscriptionPage.UpgradeToSolo)
                      : t(m.TeamSubscriptionPage.UpgradeToPro)}
                  </Menu.Item>
                  <Menu.Item onClick={openEduMoreInformation} closeOnClick>
                    {t(m.TeamSubscriptionPage.HowToRenew)}
                  </Menu.Item>
                </>
              )}
            </Menu>
          )
        }
      />
      <CTARowDivider />
      <CTARow title={t(m.TeamSubscriptionPage.PurchasedVia)} text={purchaseSource} />

      <Modal
        open={isSeatManagementModalOpen}
        title={t(m.TeamSubscriptionPage.AddSeatsTitle)}
        size="small"
        onClose={() => setIsSeatManagementModalOpen(false)}
      >
        <div className={style.notAvailableModal}>
          <span>
            {t(m.TeamSubscriptionPage.AddingSeatsUnavailable, { teamName: teamData?.name })}
          </span>
          <div className={style.message}>
            <span>{t(m.TeamSubscriptionPage.LearnMoreCollaborating)}</span>
          </div>
          <Button onClick={navigation.openTeamsArticle}>
            {t(m.TeamSubscriptionPage.LearnMore)}
          </Button>
        </div>
      </Modal>

      <CancellationFlow
        open={isCancellationFlowOpen}
        onClose={() => setIsCancellationFlowOpen(false)}
        onCancelSubscription={() => handleAutoRenewChange(false)}
        isLoading={isAutoRenewLoading || isCancelPending}
        editorSeatCount={1}
        isSubscriptionInGracePeriod={isInGracePeriod()}
        subscriptionExpirationDate={expirationDate}
        subscriptionTier={tier}
      />

      <GenericErrorModal open={isErrorModalOpen} onClose={() => setIsErrorModalOpen(false)} />
    </div>
  );
}
